import React, { useState, useEffect, useRef } from 'react';
import { useUser } from '@clerk/clerk-react';
import { useAction, useQuery } from 'convex/react';
import { api } from '../convex';

const PaymentVerification = ({ 
  subscriptionStatus, 
  setSubscriptionStatus, 
  onVerificationComplete,
  isPostPayment = false 
}) => {
  const { user } = useUser();
  const [isVerifying, setIsVerifying] = useState(false);
  const [verificationMessage, setVerificationMessage] = useState('');
  const [verificationError, setVerificationError] = useState('');
  const [pollCount, setPollCount] = useState(0);
  
  const pollIntervalRef = useRef(null);
  const timeoutRef = useRef(null);
  
  const POLL_INTERVAL = 4000; // 4 seconds
  const MAX_POLL_TIME = 60000; // 60 seconds
  const MAX_POLL_COUNT = MAX_POLL_TIME / POLL_INTERVAL; // 15 attempts

  // Correct Convex query for subscription status
  // Safely call Convex query only when we have a userId. Passing undefined fn skips the query without errors.
  const userId = user?.id;
  const convexSubStatus = useQuery(
    userId ? api.payments.getUserSubscriptionStatus : undefined,
    userId ? { userId } : undefined
  );

  // Action to force sync from Polar after payment success
  const syncFromPolar = useAction(api.actions.payments.syncSubscriptionFromPolar);

  const checkSubscriptionStatus = async () => {
    try {
      console.log(`🔄 Payment verification attempt ${pollCount + 1}/${MAX_POLL_COUNT}`);
      
      if (!convexSubStatus) {
        console.log('⚠️ Convex subscription status not loaded yet');
        return false;
      }

      console.log('📊 Convex subscription status:', convexSubStatus);
      
      // Map Convex status to expected format
      const mappedStatus = {
        has_access: convexSubStatus.hasSubscription && (convexSubStatus.status === 'active' || convexSubStatus.status === 'trialing'),
        status: convexSubStatus.status || 'inactive',
        plan_type: convexSubStatus.billingCycle || 'monthly',
        plan_name: convexSubStatus.billingCycle === 'yearly' ? 'Yearly Plan' : 'Monthly Plan',
        source: 'convex'
      };
      
      // Update the subscription status
      if (setSubscriptionStatus) {
        setSubscriptionStatus(mappedStatus);
      }
      
      // Check if subscription is now active
      if (mappedStatus.has_access && (mappedStatus.status === 'active' || mappedStatus.status === 'trialing')) {
        console.log('✅ Payment verified! Subscription is now active');
        
        setVerificationMessage('✅ Payment verified successfully! Unlocking features...');
        
        // Stop polling
        clearPolling();
        
        // Wait a moment to show success message, then complete verification
        setTimeout(() => {
          setIsVerifying(false);
          setVerificationMessage('');
          if (onVerificationComplete) {
            onVerificationComplete(mappedStatus);
          }
        }, 2000);
        
        return true; // Verification successful
      }
    } catch (error) {
      console.error('❌ Error checking subscription status:', error);
    }
    
    return false; // Verification not yet successful
  };

  const startPolling = async () => {
    console.log('🚀 Starting payment verification polling...');
    
    setIsVerifying(true);
    setVerificationError('');
    setVerificationMessage('🔄 Verifying payment, please wait...');
    setPollCount(0);

    // Attempt a forced sync from Polar once at start (best-effort)
    try {
      if (syncFromPolar && user) {
        await syncFromPolar({ userId: user.id });
      }
    } catch (e) {
      console.warn('⚠️ Sync from Polar failed (will keep polling):', e?.message || e);
    }
    
    // Start polling immediately
    checkSubscriptionStatus();
    
    // Set up polling interval
    pollIntervalRef.current = setInterval(async () => {
      setPollCount(prev => {
        const newCount = prev + 1;
        
        // Continue polling
        checkSubscriptionStatus();

        // Secondary, attempt another sync midway if still not active
        if (newCount === Math.floor(MAX_POLL_COUNT / 2) && syncFromPolar && user) {
          try { syncFromPolar({ userId: user.id }); } catch {}
        }

        // Check if we've exceeded max attempts
        if (newCount >= MAX_POLL_COUNT) {
          console.log('⏰ Payment verification timeout reached');
          handleVerificationTimeout();
        }
        return newCount;
      });
    }, POLL_INTERVAL);
    
    // Set up timeout as backup
    timeoutRef.current = setTimeout(() => {
      handleVerificationTimeout();
    }, MAX_POLL_TIME);
  };

  const handleVerificationTimeout = () => {
    console.log('⏰ Payment verification timed out');
    
    clearPolling();
    setIsVerifying(false);
    setVerificationMessage('');
    setVerificationError(
      '⏱️ Payment verification is taking longer than expected. ' +
      'Please refresh the page in a minute or contact support if the issue persists. ' +
      'Your payment may still be processing.'
    );
  };

  const clearPolling = () => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  const retryVerification = () => {
    setVerificationError('');
    startPolling();
  };

  // Auto-start verification if this is a post-payment scenario
  useEffect(() => {
    // Check URL parameters for payment success indicators
    const urlParams = new URLSearchParams(window.location.search);
    const paymentSuccess = urlParams.get('payment') === 'success' || 
                          urlParams.get('payment_success') === 'true' ||
                          urlParams.get('checkout') === 'success' ||
                          isPostPayment;
    
    // Clean up URL parameters immediately if payment success is detected
    if (paymentSuccess) {
      console.log('🧹 Cleaning up payment URL parameters...');
      window.history.replaceState({}, document.title, window.location.pathname);
    }
    
    // Only start verification if:
    // 1. It's a post-payment scenario, AND
    // 2. Current subscription doesn't have access, AND
    // 3. User is loaded
    if (paymentSuccess && user && (!subscriptionStatus?.has_access || subscriptionStatus?.status === 'inactive')) {
      console.log('🎯 Post-payment detected, starting verification...');
      startPolling();
    }
    
    // Cleanup on unmount
    return () => {
      clearPolling();
    };
  }, [isPostPayment, user]);

  // Don't render anything if not verifying and no error
  if (!isVerifying && !verificationError) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4 text-center">
        {isVerifying && (
          <>
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Payment Verification</h3>
            <p className="text-gray-600 mb-4">{verificationMessage}</p>
            <div className="text-sm text-gray-500">
              Attempt {pollCount + 1} of {MAX_POLL_COUNT}
            </div>
            <div className="mt-4">
              <button
                onClick={() => {
                  clearPolling();
                  setIsVerifying(false);
                  setVerificationMessage('');
                }}
                className="text-gray-500 hover:text-gray-700 text-sm underline"
              >
                Cancel verification
              </button>
            </div>
          </>
        )}
        
        {verificationError && (
          <>
            <div className="text-red-500 text-4xl mb-4">⚠️</div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Verification Timeout</h3>
            <p className="text-gray-600 mb-6">{verificationError}</p>
            <div className="flex gap-3 justify-center">
              <button
                onClick={retryVerification}
                className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 transition-colors"
              >
                Try Again
              </button>
              <button
                onClick={() => setVerificationError('')}
                className="bg-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-400 transition-colors"
              >
                Close
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PaymentVerification;